import { BaseEntity } from "../base/base-entity";
import {
  ProcedureDetail,
  ProcedureDetailType,
  PublishSetting,
} from "./procedure-detail";
import {
  RequireAuthLevel,
  PublishedProcedureDetail,
} from "./published-procedure-detail";

// In megabytes.
export const MaxFileSize = 100;

export type BaseProcedureType = {
  readonly id: string;
  readonly version: number;
  readonly orgId: string;
  summary: string | undefined;
  detailURL: string | undefined;
  descriptionMarkdown: string;
  name: string;
  groupIds: string[];
  publishSetting: PublishSetting;
  publishStatus: PublishStatus;
  readonly published?: Pick<ProcedureDetailType, "requireAuthLevel">;
  isGrafferManaged: boolean;
  updatedAt: Date;
  archivedAtUnixTime: number;
  archivedBy: string;
};

export type ProcedureType = BaseProcedureType & {
  customTag: CustomTag | undefined;
  draft: ProcedureDetailType;
  readonly published?: ProcedureDetailType;
  isExcludeFromSearch: boolean;
  isSkipArrivalMail: boolean;
  isLinkTopPage: boolean;
  formAccessRestriction?: FormAccessRestriction;
  customerDlConfig?: CustomerDlConfig;
};

export class BaseProcedureEntity extends BaseEntity {
  public readonly id: string;
  public readonly version: number;
  public readonly orgId: string;
  public summary: string | undefined;
  public detailURL: string | undefined;
  public descriptionMarkdown: string;
  public name: string;
  public groupIds: string[];
  public publishSetting: PublishSetting;
  public publishStatus: PublishStatus;
  public readonly published?: Pick<ProcedureDetailType, "requireAuthLevel">;
  public isGrafferManaged: boolean;
  public updatedAt: Date;
  public archivedAtUnixTime: number;
  public archivedBy: string;

  constructor(values: BaseProcedureType) {
    super();
    this.id = values.id;
    this.version = values.version;
    this.orgId = values.orgId;
    this.groupIds = values.groupIds;
    this.summary = values.summary;
    this.detailURL = values.detailURL;
    this.descriptionMarkdown = values.descriptionMarkdown;
    this.name = values.name;
    this.publishSetting = values.publishSetting;
    this.publishStatus = values.publishStatus;
    this.published = values.published
      ? new RequireAuthLevel(values.published)
      : undefined;
    this.isGrafferManaged = values.isGrafferManaged;
    this.updatedAt = values.updatedAt;
    this.archivedAtUnixTime = values.archivedAtUnixTime;
    this.archivedBy = values.archivedBy;
  }
}

export class ProcedureEntity extends BaseProcedureEntity {
  public customTag: CustomTag | undefined;
  public draft: ProcedureDetail;
  public readonly published?: PublishedProcedureDetail;
  public isExcludeFromSearch: boolean;
  public isSkipArrivalMail: boolean;
  public isLinkTopPage: boolean;
  public formAccessRestriction?: FormAccessRestriction;
  public customerDlConfig?: CustomerDlConfig;

  constructor(values: ProcedureType) {
    super({
      ...values,
    });
    this.customTag = values.customTag;
    this.draft = new ProcedureDetail(values.draft);
    this.published = values.published
      ? new PublishedProcedureDetail(values.published)
      : undefined;
    this.isExcludeFromSearch = values.isExcludeFromSearch;
    this.isSkipArrivalMail = values.isSkipArrivalMail;
    this.isLinkTopPage = values.isLinkTopPage;
    this.formAccessRestriction = values.formAccessRestriction;
    this.customerDlConfig = values.customerDlConfig;
  }
}

export const CustomTags = [
  "safenet-crisis",
  "safenet-crisis-from-bank",
  "safenet-crisis-four",
  "safenet-crisis-four-from-bank",
  "rent-reduction-subsidy",
  "smb-Challenge",
  "smb-Challenge-report",
  "preschool-free",
  "preschool-free-e",
  "school-commuter-pass",
  "industrial-waste-status-report",
  "accommodation-tax",
  "prescription",
  "pre-construction-structure",
  "pre-construction-building",
  "recruitment-separate",
  "elderly-ticket-r2",
  "elderly-and-welfare-ticket-r3",
  "elderly-and-welfare-ticket-r4",
  "elderly-and-welfare-ticket-r5",
  "elderly-and-welfare-ticket-r6",
  "elderly-and-welfare-ticket-r7",
  "yachin-support",
  "yachin-support-v2",
  "recruitment-advanced",
  "safenet-crisis-five-pattern-ione",
  "safenet-crisis-five-pattern-ifour",
  "safenet-crisis-five-pattern-ione-from-bank",
  "safenet-crisis-five-pattern-ifour-from-bank",
  "recruitment-beginner",
  "recruitment-disability",
] as const;

export type CustomTag = (typeof CustomTags)[number];

export const PublishStatusList = [
  "DRAFT",
  "BEFORE_PUBLISH",
  "BEFORE_ACCEPTING",
  "ACCEPTING",
  "PAUSING_ACCEPT",
  "FINISHED_ACCEPTING",
  "UNPUBLISHED",
] as const;
export type PublishStatus = (typeof PublishStatusList)[number];

export type AuthLevelIndividual = "NONE" | "MY_NUMBER_CARD_SIGNATURE";
export type AuthLevelCorporation = "NONE" | "MOJ_SIGNATURE";

export type FormAccessRestriction = {
  passcode?: string;
};

export type CustomerDlConfig = {
  pdfDownloadConfig?: PDFDownloadConfig;
};

export type PDFDownloadConfig = {
  fileName: string;
  description?: string;
};
